import * as THREE from 'three';
import FastSimplexNoise from './noise';
import { Osc } from './osc';
import { SystemBase } from './system_base';

export class System extends SystemBase {
    private simplex = new FastSimplexNoise();
    private color = new THREE.Color();
    // Remove texture-related properties
    // private texture: THREE.Texture;
    private size = 10;
    private base = 18;
    private count = this.base ** 3;
    
    // Replace geometry and material with sphere-based approach
    public sphereGeometry = new THREE.SphereGeometry(0.02, 8, 6); // Small sphere geometry
    private sphereMaterial: THREE.MeshBasicMaterial;
    private sphereInstances: THREE.InstancedMesh;
    
    private parts: any[] = [];
    private osc: Osc;
    private oscEased = 0;

    // Remove buffer attributes for points
    // private positions = new Float32Array(this.count * 3);
    // private colors = new Float32Array(this.count * 4);
    // private sizes = new Float32Array(this.count);

    // Add instance matrices for spheres
    private instanceMatrix = new THREE.InstancedBufferAttribute(new Float32Array(this.count * 16), 16);
    private instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(this.count * 3), 3);
    private instanceScale = new THREE.InstancedBufferAttribute(new Float32Array(this.count), 1);

    constructor(loader: any) {
        super(loader);
        
        // Create sphere material instead of texture
        this.sphereMaterial = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.95
        });

        // Create instanced mesh for spheres
        this.sphereInstances = new THREE.InstancedMesh(
            this.sphereGeometry,
            this.sphereMaterial,
            this.count
        );

        // Set up instance attributes
        this.sphereInstances.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
        // instanceColor may be null depending on THREE.js version/config
        if (this.sphereInstances.instanceColor) {
            this.sphereInstances.instanceColor.setUsage(THREE.DynamicDrawUsage);
        }
        // instanceScale is not a built-in property on InstancedMesh, so remove or comment this line
        // this.sphereInstances.instanceScale.setUsage(THREE.DynamicDrawUsage);

        // particle setup
        for (let i = 0; i < this.count; i++) {
            const size = this.calc.rand(0.4, 0.8);
            this.parts.push({
                position: new THREE.Vector3(
                    this.calc.rand(-this.size / 2, this.size / 2),
                    this.calc.rand(-this.size / 2, this.size / 2),
                    this.calc.rand(-this.size / 2, this.size / 2)
                ),
                baseSize: size,
                size,
                r: 1, g: 1, b: 1, a: 0,
                life: 2,
                decay: this.calc.rand(0.001, 0.008),
                firstRun: true
            });
        }

        this.particleGroup.add(this.sphereInstances);
        this.osc = new Osc(0, 0.005);
    }

    // Remove generateTexture method
    // private generateTexture() { ... }

    updateParticleAttributes() {
        const matrix = new THREE.Matrix4();
        const position = new THREE.Vector3();
        const scale = new THREE.Vector3();
        const color = new THREE.Color();

        for (let i = 0; i < this.count; i++) {
            const part = this.parts[i];
            
            // Set position
            position.set(part.position.x, part.position.y, part.position.z);
            
            // Set scale
            const scaleValue = part.size;
            scale.setScalar(scaleValue);
            
            // Set color
            color.setRGB(part.r, part.g, part.b);
            color.multiplyScalar(part.a); // Apply alpha
            
            // Update instance matrix
            matrix.compose(position, new THREE.Quaternion(), scale);
            this.sphereInstances.setMatrixAt(i, matrix);
            
            // Update instance color
            this.sphereInstances.setColorAt(i, color);
        }

        // Mark for update
        this.sphereInstances.instanceMatrix.needsUpdate = true;
        this.sphereInstances.instanceColor!.needsUpdate = true;
    }

    update() {
        super.update();

        this.osc.update(this.loader.deltaTimeNormal);
        this.oscEased = this.osc.val(this.ease.inOutExpo);

        let i = this.count;

        let noiseScale = 0.03;
        let noiseTime = this.loader.elapsedMilliseconds * 0.0006;
        let noiseVelocity = this.calc.map(this.oscEased, 0, 1, 0, 1);

        while (i--) {
            let part = this.parts[i];

            let xScaled = part.position.x * noiseScale;
            let yScaled = part.position.y * noiseScale;
            let zScaled = part.position.z * noiseScale;

            let noise1 = this.simplex.getRaw4DNoise(
                xScaled,
                yScaled,
                zScaled,
                noiseTime
            ) * 0.5 + 0.5;
            let noise2 = this.simplex.getRaw4DNoise(
                xScaled + 100,
                yScaled + 100,
                zScaled + 100,
                50 + noiseTime
            ) * 0.5 + 0.5;
            let noise3 = this.simplex.getRaw4DNoise(
                xScaled + 200,
                yScaled + 200,
                zScaled + 200,
                100 + noiseTime
            ) * 0.5 + 0.5;

            part.position.x += Math.sin(noise1 * Math.PI * 2) * noiseVelocity * this.loader.deltaTimeNormal * 0.3;
            part.position.y += Math.sin(noise2 * Math.PI * 2) * noiseVelocity * this.loader.deltaTimeNormal * 0.3;
            part.position.z += Math.sin(noise3 * Math.PI * 2) * noiseVelocity * this.loader.deltaTimeNormal * 0.3;

            if (part.life > 0) {
                part.life -= part.decay * this.oscEased * this.loader.deltaTimeNormal;
            }

            if (part.life <= 0 || part.firstRun) {
                part.life = 2;
                part.position.x = this.calc.rand(-this.size / 2, this.size / 2);
                part.position.y = this.calc.rand(-this.size / 2, this.size / 2);
                part.position.z = this.calc.rand(-this.size / 2, this.size / 2);

                if (this.loader.particlePalette === "hero") {
                    // Cyan–teal field with occasional brand-orange sparks (quantum landing hero)
                    const roll = this.calc.rand(0, 1);
                    if (roll < 0.68) {
                        this.color.setHSL(
                            this.calc.rand(0.46, 0.58),
                            this.calc.rand(0.62, 0.92),
                            this.calc.rand(0.42, 0.62)
                        );
                    } else if (roll < 0.9) {
                        this.color.set(this.calc.rand(0, 1) > 0.5 ? "#FF5A1F" : "#D03E00");
                    } else {
                        this.color.set("#7dd3fc");
                    }
                } else {
                    this.color.set("#c05621");
                }

                part.r = this.color.r;
                part.g = this.color.g;
                part.b = this.color.b;

                part.firstRun = false;
            }

            part.a = part.life > 1 ? 2 - part.life : part.life;
            part.size = this.calc.map(this.oscEased, 0, 1, part.baseSize * 3, part.baseSize * 4);
        }

        this.updateParticleAttributes();

        this.particleGroup.rotation.y += (0.002 + this.oscEased * 0.01) * this.loader.deltaTimeNormal;
        this.particleGroup.position.z = 5 - this.oscEased * 15;
    }
}
